import React, { useMemo, useRef, useState } from 'react';
import { Navigation, Pagination } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';

import OutfitCard, { OutfitCardSkeleton } from './outfit-card';
import OutfitsPopup from './popup';

import useOutfitProducts from './use-outfit-products';

import 'swiper/css';

import { cn } from '../shared/utils';
import Logo from './logo';
import styles from './outfits.module.scss';
import './swiper-modules.scss';

const Outfits = ({ data, gaBlockName = 'Seven Outfits', userSession }) => {
  // toggle between user flows // product markers overlay & popup or popup only
  const purchaseIntent = true;

  const [activeIdx, setActiveIdx] = useState(0);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [isSwiperLocked, setIsSwiperLocked] = useState(false);
  const [isOverlayPinnedOpen, setIsOverlayPinnedOpen] = useState(false);

  const paginationRef = useRef(null);

  const outfitProdIds = useMemo(
    () =>
      (data?.outfits || []).map((item) =>
        (item?.products || []).map((product) => product.prod_id)
      ),
    [data]
  );

  const productIds = outfitProdIds[activeIdx] || [];
  const { products: activeProducts, loading: isProductsLoading } =
    useOutfitProducts({
      prodIds: productIds,
      enabled: Boolean(data?.outfits?.length)
    });

  const currentBg =
    data && data?.outfits[activeIdx]?.background_colour
      ? data?.outfits[activeIdx]?.background_colour
      : '#121212';

  console.log('appearance', data && data.appearance);

  return (
    <section
      className={`${styles.container} outfits-section ${
        purchaseIntent ? 'purchase-intent-enabled' : ''
      } ${isOverlayPinnedOpen ? 'overlay-pinned-open' : ''}`}
      style={{
        backgroundColor: data && currentBg,
        transition: 'background 0.75s ease'
      }}
    >
      <Title
        appearance={data?.appearance || 'light'}
        text={gaBlockName}
        prodCount={data?.outfits?.length}
        activeIndex={activeIdx}
      />
      <Swiper
        key={data?.outfits?.length || 0}
        slidesPerView={1.2}
        spaceBetween={-80}
        centeredSlides={true}
        loop={data?.outfits?.length > 1}
        mousewheel={{
          forceToAxis: true,
          sensitivity: 1,
          releaseOnEdges: false
        }}
        modules={[Pagination, Navigation]}
        pagination={{
          el: paginationRef.current,
          clickable: true,
          renderBullet: (index, className) =>
            `<span class="${className}" aria-label="Go to slide ${
              index + 1
            }"></span>`
        }}
        onBeforeInit={(swiper) => {
          swiper.params.pagination.el = paginationRef.current;
        }}
        onSwiper={(swiper) => {
          if (paginationRef.current) {
            paginationRef.current.style.setProperty(
              '--active-index',
              `${swiper.realIndex}`
            );
            paginationRef.current.style.setProperty(
              '--bullet-count',
              `${
                swiper.slides.length - swiper.loopedSlides * 2 ||
                data?.outfits?.length ||
                1
              }`
            );
          }
        }}
        navigation={{
          nextEl: '.swiper-button-next',
          prevEl: '.swiper-button-prev'
        }}
        onSlideChangeTransitionStart={(swiper) => {
          setActiveIdx(swiper.realIndex);
          if (paginationRef.current) {
            paginationRef.current.style.setProperty(
              '--active-index',
              `${swiper.realIndex}`
            );
          }
        }}
        breakpoints={{
          768: {
            spaceBetween: 0,
            slidesPerView: 2.5
          }
        }}
        allowTouchMove={!isSwiperLocked}
        allowSlideNext={!isSwiperLocked}
        allowSlidePrev={!isSwiperLocked}
      >
        {data?.outfits?.length > 0
          ? data.outfits.map((item, index) => (
              <SwiperSlide
                className={styles.swiperSlide}
                key={item?.id || item?.outfit_id || item?.image || index}
              >
                <OutfitCard
                  appearance={data?.appearance}
                  data={item}
                  isActive={index === activeIdx}
                  userSession={userSession}
                  setIsPopupOpen={setIsPopupOpen}
                  setIsSwiperLocked={setIsSwiperLocked}
                  purchaseIntent={purchaseIntent}
                  productsData={activeProducts}
                  isProductsLoading={isProductsLoading}
                  isOverlayPinnedOpen={isOverlayPinnedOpen}
                  setIsOverlayPinnedOpen={setIsOverlayPinnedOpen}
                />
              </SwiperSlide>
            ))
          : Array.from({ length: 3 }).map((_, index) => (
              <SwiperSlide key={index}>
                <OutfitCardSkeleton isActive={index === activeIdx} />
              </SwiperSlide>
            ))}
      </Swiper>
      <div
        className={cn(
          'swiper-buttons',
          data?.appearance === 'light' ? 'light-mode' : 'dark-mode'
        )}
      >
        <div className="swiper-button-prev">
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M14.2501 8C14.2501 8.19892 14.1711 8.38968 14.0304 8.53033C13.8898 8.67099 13.699 8.75 13.5001 8.75H4.3126L7.5326 11.9694C7.6735 12.1103 7.75265 12.3014 7.75265 12.5006C7.75265 12.6999 7.6735 12.891 7.5326 13.0319C7.39171 13.1728 7.20061 13.2519 7.00135 13.2519C6.8021 13.2519 6.611 13.1728 6.4701 13.0319L1.9701 8.53188C1.90018 8.4622 1.84471 8.37941 1.80685 8.28824C1.769 8.19708 1.74951 8.09934 1.74951 8.00063C1.74951 7.90192 1.769 7.80418 1.80685 7.71302C1.84471 7.62185 1.90018 7.53906 1.9701 7.46938L6.4701 2.96938C6.53987 2.89961 6.62269 2.84427 6.71384 2.80652C6.80499 2.76876 6.90269 2.74933 7.00135 2.74933C7.10002 2.74933 7.19771 2.76876 7.28886 2.80652C7.38002 2.84427 7.46284 2.89961 7.5326 2.96938C7.60237 3.03914 7.65771 3.12197 7.69546 3.21312C7.73322 3.30427 7.75265 3.40197 7.75265 3.50063C7.75265 3.59929 7.73322 3.69699 7.69546 3.78814C7.65771 3.87929 7.60237 3.96211 7.5326 4.03188L4.3126 7.25H13.5001C13.699 7.25 13.8898 7.32902 14.0304 7.46967C14.1711 7.61033 14.2501 7.80109 14.2501 8Z"
              fill="currentColor"
            />
          </svg>
        </div>
        <div
          ref={paginationRef}
          className="swiper-pagination outfits-pagination"
        />
        <div className="swiper-button-next">
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M14.0306 8.53063L9.53063 13.0306C9.38973 13.1715 9.19863 13.2507 8.99938 13.2507C8.80012 13.2507 8.60902 13.1715 8.46813 13.0306C8.32723 12.8897 8.24807 12.6986 8.24807 12.4994C8.24807 12.3001 8.32723 12.109 8.46813 11.9681L11.6875 8.75H2.5C2.30109 8.75 2.11032 8.67098 1.96967 8.53033C1.82902 8.38968 1.75 8.19892 1.75 8C1.75 7.80109 1.82902 7.61032 1.96967 7.46967C2.11032 7.32902 2.30109 7.25 2.5 7.25H11.6875L8.46937 4.03C8.32848 3.88911 8.24932 3.69801 8.24932 3.49875C8.24932 3.2995 8.32848 3.1084 8.46937 2.9675C8.61027 2.82661 8.80137 2.74745 9.00062 2.74745C9.19988 2.74745 9.39098 2.82661 9.53187 2.9675L14.0319 7.4675C14.1018 7.53727 14.1573 7.62017 14.1951 7.71144C14.2329 7.80271 14.2523 7.90054 14.2522 7.99932C14.252 8.09811 14.2324 8.1959 14.1944 8.28708C14.1564 8.37825 14.1007 8.46102 14.0306 8.53063Z"
              fill="currentColor"
            />
          </svg>
        </div>
      </div>
      {isPopupOpen && (
        <OutfitsPopup
          outfit={data?.outfits?.[activeIdx]}
          products={activeProducts}
          isProductsLoading={isProductsLoading}
          isPopupOpen={isPopupOpen}
          setIsPopupOpen={setIsPopupOpen}
          userSession={userSession}
          buttonInfo={{
            url: data?.outfits?.[activeIdx]?.button_url,
            text: data?.outfits?.[activeIdx]?.button_text
          }}
        />
      )}
    </section>
  );
};

const Title = ({ appearance = 'light', text, prodCount, activeIndex }) => (
  <div className="relative w-full mx-auto lg:flex lg:flex-row lg:justify-between lg:items-center lg:w-[45%] lg:absolute lg:left-[47%] lg:top-36 z-10 pointer-events-none lg:-translate-x-1/2">
    <div className="flex flex-row gap-3 mx-auto pb-4 lg:mx-0 lg:pb-0 w-fit">
      <div
        className={`w-fit py-3 px-5 
          ${
            appearance === 'light'
              ? 'bg-white/60 backdrop-blur-sm text-black'
              : 'text-white'
          }`}
      >
        <Logo.Seven />
      </div>
      <div
        className={`w-fit bg-black/40 backdrop-blur-sm py-3 px-5 text-white`}
      >
        <Logo.Outfits />
      </div>
    </div>
    <span className="sr-only">{text}</span>
    <div className="absolute right-16 lg:right-4 top-20 bg-black/20 backdrop-blur-md py-3 px-6 flex flex-row gap-6 z-10 text-white items-center !text-md lg:static">
      <span>{activeIndex + 1}</span>
      <span className="w-6 h-px bg-white rotate-90" />
      <span>{prodCount}</span>
    </div>
  </div>
);

export default Outfits;
